import React from "react";
import { Link } from "react-router-dom";
import Table from "../../../components/Table";
import { useBranding } from "../../../branding/BrandingContextProvider";
import Status from "../../../components/Status";
import IcoMoon from "../../../components/IcoMoon";
import { ICONS, PATHS } from "../../../common/constants";
import Checkbox from "../../../components/Checkbox";
import Icon from "../../../components/Icon";
import { sortByDate } from "../../../components/Table/sorting";
import moment from "moment";
import { convertTime } from "../../../helpers/time";
import Label from "../../../components/Label";

const GroupedHeaderControls = ({
  data,
  onExpandGroup,
  onContractGroup,
  isSelected,
  onSelectGroup,
  onDeleteGroup,
}) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <Checkbox
      onChange={() => onSelectGroup()}
      name={data.id}
      value={isSelected}
      readOnly
    />

    {data.isExpanded ? (
      <div
        style={{ cursor: "pointer" }}
        onClick={() => onContractGroup(data.id)}
      >
        <Icon glyph="angle-up" weight="regular" size="big" />
      </div>
    ) : (
      <div style={{ cursor: "pointer" }} onClick={() => onExpandGroup(data.id)}>
        <Icon glyph="angle-down" weight="regular" size="big" />
      </div>
    )}

    <div
      style={{ cursor: "pointer", marginLeft: "1rem" }}
      onClick={() => onDeleteGroup(data.id)}
    >
      <Icon glyph="object-ungroup" weight="regular" size="big" />
    </div>
  </div>
);

export const ServiceRequestDashboardTable = ({
  serviceRequests,
  selectedServiceRequests,
  onSelectServiceRequest,
  onSelectGroup,
  onDeleteGroup,
  onRemoveFromGroup,
  onSelectAll,
  allSelected,
}) => {
  const { branding } = useBranding();
  const [expandedGroups, setExpandedGroups] = React.useState([]);
  return (
    <Table
      expandedGroups={expandedGroups}
      applyMinWidth
      defaultSort={
        {
          accessor: "createdDate", 
          direction: "ascending"
        }
      }
      columns={[
        {
          label: <Checkbox readOnly value={allSelected} onChange={onSelectAll} />,
          style: { width: "5rem" },
          minWidth: "5rem",
          groupedHeaderCell: (data) => (
            <GroupedHeaderControls
              data={data}
              onExpandGroup={() =>
                setExpandedGroups([...expandedGroups, data.id])
              }
              onContractGroup={() =>
                setExpandedGroups(expandedGroups.filter((id) => id !== data.id))
              }
              isSelected={
                allSelected ||
                data.children.every((c) =>
                  selectedServiceRequests.includes(c.id)
                )
              }
              onSelectGroup={() =>
                onSelectGroup(data.children.map((c) => c.id))
              }
              onDeleteGroup={() => onDeleteGroup(data.id)}
            />
          ),
          cell: (data) => (
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {data.isChild && (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => onRemoveFromGroup(data.id, data.groupId)}
                >
                  <Icon glyph="object-ungroup" weight="regular" size="big" />
                </div>
              )}
              <Checkbox
                checked={
                  selectedServiceRequests.includes(data.id) || allSelected
                }
                name={data.id}
                value={selectedServiceRequests.includes(data.id) || allSelected}
                onChange={() => onSelectServiceRequest(data.id)}
                readOnly
              />
            </div>
          ),
        },
        {
          label: `${branding?.text?.suite} | SL#`,
          accessor: "name",
          sortable: true,
          groupedHeaderCell: (data) => data.name,
          style: { width: "7rem" },
          minWidth: "7rem",
          groupedHeaderCellSpan: 9,
          xsGroupedHeaderCellSpan: 3,
          cell: (data) => (
            <Link
              to={{
                pathname: PATHS.SERVICE_REQUESTS_VIEW.replace(":id", data.id),
                state: { navigateTo: PATHS.SERVICE_REQUESTS_LIST },
              }}
            >
              {`${data.name}` + (data.stratalot ? ` | ${data.stratalot}` : "")}
            </Link>
          ),
        },
        {
          label: "Description",
          accessor: "description",
          textOverflow: true,
          sortable: true,
          xsHide: true,
          minWidth: "55rem",
          cell: (data) => (data.description?.trim()??"")
        },
        {
          label: "Area",
          accessor: "area",
          xsHide: true,
          style: { width: "12rem" },
          minWidth: "12rem",
          sortable: true,
          
        },
        {
          label: "Asset",
          xsHide: true,
          accessor: "item",
          style: { width: "10rem" },
          minWidth: "10rem",
          sortable:true
        },
        {
          label: "Type",
          xsHide: true,
          accessor: "type",
          style: { width: "6rem" },
          minWidth: "6rem",
          sortable: true
        },
        {
          label: "Status",
          showOverflow: true,
          accessor: "status",
          style: { width: "10rem" },
          minWidth: "10rem",
          xsHide: true,
          sortable: true,
          cell: (data) => (
            <Status
              icon={<IcoMoon glyph={ICONS[data.status]} />}
              label={<div title={data.status}>{data.status}</div>}
            />
          ),
        },
        {
          label: "Submitted On",
          sortable: true,
          accessor: "createdDate",
          sortingAlgorithm: sortByDate,
          cell: (data) => {
            const days = moment().diff(moment(data.createdDate), "days");
            const color = days <= 30 ? "teal" : days <= 60 ? "yellow" : "red";
            return (
              <Label
                color={color}
                label={convertTime(data.createdDate, "date")}
              />
            );
          },
          style: { width: "10rem" },
          minWidth: "10rem",
        },
        {
          label: "Assigned To",
          accessor: "serviceProvider",
          style: { width: "8rem" },
          minWidth: "8rem",
          sortable: true,
        },
        {
          label: "Scheduled",
          accessor: "scheduledAt",
          sortable: true,
          xsHide: true,
          sortingAlgorithm: sortByDate,
          cell: (data) => isValidDate(data.scheduledAt) ? convertTime(data.scheduledAt, "date") : "",
          style: { width: "10rem" },
          minWidth: "10rem",

        }
      ]}
      data={serviceRequests}
    />
  );
};

const isValidDate = (date) => date !== null && moment(date).year() > 2020;